"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/primitives";

// =============================================================================
// Context
// =============================================================================

interface AlertDialogContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AlertDialogContext = React.createContext<AlertDialogContextValue | null>(
  null
);

function useAlertDialogContext() {
  const context = React.useContext(AlertDialogContext);
  if (!context) {
    throw new Error(
      "AlertDialog components must be used within an AlertDialog"
    );
  }
  return context;
}

// =============================================================================
// AlertDialog Root
// =============================================================================

interface AlertDialogProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
}

function AlertDialog({
  children,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
}: AlertDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [isControlled, onOpenChange]
  );

  return (
    <AlertDialogContext.Provider value={{ open, onOpenChange: handleOpenChange }}>
      {children}
    </AlertDialogContext.Provider>
  );
}

// =============================================================================
// AlertDialogTrigger
// =============================================================================

interface AlertDialogTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

function AlertDialogTrigger({ children, asChild }: AlertDialogTriggerProps) {
  const { onOpenChange } = useAlertDialogContext();

  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<{ onClick?: () => void }>, {
      onClick: () => onOpenChange(true),
    });
  }

  return (
    <button type="button" onClick={() => onOpenChange(true)}>
      {children}
    </button>
  );
}

// =============================================================================
// AlertDialogPortal (renders content)
// =============================================================================

interface AlertDialogContentProps {
  children: React.ReactNode;
  className?: string;
  size?: "default" | "sm";
}

function AlertDialogContent({
  children,
  className,
  size = "default",
}: AlertDialogContentProps) {
  const { open, onOpenChange } = useAlertDialogContext();
  const [mounted, setMounted] = React.useState(false);

  // Ensure we only render the portal on the client
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // Close on escape key
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onOpenChange(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onOpenChange]);

  // Prevent body scroll when open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open || !mounted) return null;

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        className={cn(
          "fixed left-[50%] top-[50%] z-50",
          "translate-x-[-50%] translate-y-[-50%]",
          "bg-bg-primary rounded-lg shadow-lg",
          "border border-border-primary",
          "animate-in fade-in-0 zoom-in-95",
          "duration-200",
          size === "sm" ? "w-[380px] max-w-[90vw]" : "w-[480px] max-w-[90vw]",
          className
        )}
        role="alertdialog"
        aria-modal="true"
      >
        {children}
      </div>
    </>
  );

  // Use portal to render at document body level
  return createPortal(content, document.body);
}

// =============================================================================
// AlertDialogHeader
// =============================================================================

interface AlertDialogHeaderProps {
  children: React.ReactNode;
  className?: string;
}

function AlertDialogHeader({ children, className }: AlertDialogHeaderProps) {
  return (
    <div className={cn("flex flex-col gap-2 p-6 pb-4", className)}>
      {children}
    </div>
  );
}

// =============================================================================
// AlertDialogTitle
// =============================================================================

interface AlertDialogTitleProps {
  children: React.ReactNode;
  className?: string;
}

function AlertDialogTitle({ children, className }: AlertDialogTitleProps) {
  return (
    <h2
      className={cn("text-lg font-semibold text-text-primary", className)}
    >
      {children}
    </h2>
  );
}

// =============================================================================
// AlertDialogDescription
// =============================================================================

interface AlertDialogDescriptionProps {
  children: React.ReactNode;
  className?: string;
}

function AlertDialogDescription({
  children,
  className,
}: AlertDialogDescriptionProps) {
  return (
    <p className={cn("text-sm text-text-secondary", className)}>{children}</p>
  );
}

// =============================================================================
// AlertDialogFooter
// =============================================================================

interface AlertDialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

function AlertDialogFooter({ children, className }: AlertDialogFooterProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-3 px-6 py-4 border-t",
        className
      )}
    >
      {children}
    </div>
  );
}

// =============================================================================
// AlertDialogCancel
// =============================================================================

interface AlertDialogCancelProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

function AlertDialogCancel({
  children,
  className,
  ...props
}: AlertDialogCancelProps) {
  const { onOpenChange } = useAlertDialogContext();

  return (
    <Button
      variant="secondary"
      onClick={() => onOpenChange(false)}
      className={className}
      {...props}
    >
      {children}
    </Button>
  );
}

// =============================================================================
// AlertDialogAction
// =============================================================================

interface AlertDialogActionProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: "default" | "destructive";
}

function AlertDialogAction({
  children,
  className,
  variant = "default",
  onClick,
  ...props
}: AlertDialogActionProps) {
  const { onOpenChange } = useAlertDialogContext();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    onOpenChange(false);
  };

  return (
    <Button
      variant={variant === "destructive" ? "default" : "default"}
      onClick={handleClick}
      className={cn(
        variant === "destructive" &&
          "bg-error-600 hover:bg-error-700 text-white",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );
}

// =============================================================================
// Exports
// =============================================================================

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
};
